/**
 * DiagnosticsPanel - Real-time diagnostic results display
 * 
 * Features:
 * - Auto-refresh every 30 seconds
 * - Connex Vital Signs (priority 1)
 * - ECG Results (priority 2)
 * - Spirometry Results (priority 3)
 * - Manual refresh button
 * - Modern styling with color coding
 * - Enhanced header styling with darker backgrounds
 * - Proper error handling (no toast errors for missing data)
 * 
 * Author: AI Assistant
 * Date: 2025-10-19
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Activity,
  Heart,
  Thermometer,
  Wind,
  Droplets,
  RefreshCw,
  Settings,
  Download,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  Zap,
  Loader,
  FileText,
  Eye,
  ExternalLink,
  BarChart3
} from 'lucide-react';
import apiClient from '../../services/apiClient';
import toast from 'react-hot-toast';

const DiagnosticsPanel = ({ bookingId, patientId }) => {
  // State management
  const [vitalsData, setVitalsData] = useState(null);
  const [ecgData, setEcgData] = useState(null);
  const [uploadedECG, setUploadedECG] = useState(null);
  const [uploadedSpirometry, setUploadedSpirometry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [countdown, setCountdown] = useState(30);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const intervalRef = useRef(null);
  const countdownRef = useRef(null);

  // Fetch diagnostics data
  const fetchDiagnostics = useCallback(async (silent = false) => {
    try {
      if (!silent) setRefreshing(true);

      // Fetch Connex vitals
      let vitalsResponse = null;
      try {
        vitalsResponse = await apiClient.get(
          `/connex-integration/vitals/booking/${bookingId}`
        );
        setVitalsData(vitalsResponse.data);
      } catch (err) {
        console.log('No Connex vitals available yet');
        setVitalsData(null);
      }

      // Fetch ECG results
      let ecgResponse = null;
      try {
        ecgResponse = await apiClient.get(
          `/ecg-integration/results/booking/${bookingId}`
        );
        setEcgData(ecgResponse?.data || null);
      } catch (err) {
        console.log('No ECG data available yet');
        setEcgData(null);
      }

      // Fetch uploaded ECG file (if exists)
      let uploadedECGResponse = null;
      try {
        uploadedECGResponse = await apiClient.get(
          `/ecg-results/view/${bookingId}`
        );
        setUploadedECG(uploadedECGResponse?.data?.data || null);
      } catch (err) {
        console.log('No uploaded ECG file available');
        setUploadedECG(null);
      }

      // Fetch uploaded Spirometry file (if exists)
      let uploadedSpirometryResponse = null;
      try {
        uploadedSpirometryResponse = await apiClient.get(
          `/spirometry-results/view/${bookingId}`
        );
        setUploadedSpirometry(uploadedSpirometryResponse?.data?.data || null);
      } catch (err) {
        console.log('No uploaded Spirometry file available');
        setUploadedSpirometry(null);
      }

      setLastUpdate(new Date());
      setCountdown(30);

      if (!silent) {
        toast.success('Diagnostics updated', { duration: 2000 });
      }
    } catch (error) {
      console.error('Error fetching diagnostics:', error);
      // Don't show error toast for missing data - it's not an error
      if (!silent && error.response?.status !== 404) {
        toast.error('Failed to fetch diagnostics');
      }
    } finally {
      if (!silent) setRefreshing(false);
      setLoading(false);
    }
  }, [bookingId]);

  // Initial load
  useEffect(() => {
    fetchDiagnostics();
  }, [fetchDiagnostics]);

  // Auto-refresh setup
  useEffect(() => {
    if (!autoRefreshEnabled) return;

    // Refresh data every 30 seconds
    intervalRef.current = setInterval(() => {
      fetchDiagnostics(true);
    }, 30000);

    // Countdown timer
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? 30 : prev - 1));
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [autoRefreshEnabled, fetchDiagnostics]);

  // Manual refresh
  const handleManualRefresh = () => {
    setCountdown(30);
    fetchDiagnostics(false);
  };

  // Toggle auto-refresh
  const toggleAutoRefresh = () => {
    setAutoRefreshEnabled(!autoRefreshEnabled);
    toast.success(
      autoRefreshEnabled ? 'Auto-refresh disabled' : 'Auto-refresh enabled',
      { duration: 2000 }
    );
  };

  // Get vital status
  const getVitalStatus = (value, min, max) => {
    if (value >= min && value <= max) {
      return { status: 'normal', color: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle };
    } else if (value < min * 0.9 || value > max * 1.1) {
      return { status: 'critical', color: 'text-red-600', bg: 'bg-red-50', icon: AlertTriangle };
    } else {
      return { status: 'warning', color: 'text-yellow-600', bg: 'bg-yellow-50', icon: AlertTriangle };
    }
  };

  // Calculate progress percentage
  const getProgressPercentage = (value, min, max) => {
    return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
  };

  // Format time ago
  const getTimeAgo = (date) => {
    if (!date) return 'Never';
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading diagnostics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-gray-50">
      {/* Header with controls */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center space-x-2">
              <Activity className="w-6 h-6 text-blue-600" />
              <span>Live Diagnostics Dashboard</span>
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Last updated: {lastUpdate ? getTimeAgo(lastUpdate) : 'Never'}
            </p>
          </div>

          <div className="flex items-center space-x-3">
            {/* Auto-refresh status */}
            <div className="flex items-center space-x-2 px-3 py-2 bg-gray-100 rounded-lg">
              <div className={`w-2 h-2 rounded-full ${autoRefreshEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              <span className="text-sm text-gray-700">
                {autoRefreshEnabled ? `Next: ${countdown}s` : 'Paused'}
              </span>
            </div>

            {/* Manual refresh */}
            <button
              onClick={handleManualRefresh}
              disabled={refreshing}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh now"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
            </button>

            {/* Auto-refresh toggle */}
            <button
              onClick={toggleAutoRefresh}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={autoRefreshEnabled ? 'Pause auto-refresh' : 'Enable auto-refresh'}
            >
              {autoRefreshEnabled ? (
                <Zap className="w-5 h-5 text-green-600" />
              ) : (
                <Zap className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {/* Settings */}
            <button
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Settings"
            >
              <Settings className="w-5 h-5 text-gray-600" />
            </button>

            {/* Export */}
            <button
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Export all"
            >
              <Download className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        {autoRefreshEnabled && (
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-1">
              <div
                className="bg-blue-600 h-1 rounded-full transition-all duration-1000"
                style={{ width: `${((30 - countdown) / 30) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Connex Vitals Card */}
      <ConnexVitalsCard vitalsData={vitalsData} getVitalStatus={getVitalStatus} getProgressPercentage={getProgressPercentage} />

      {/* ECG Results Card */}
      <ECGResultsCard ecgData={ecgData} uploadedECG={uploadedECG} bookingId={bookingId} />

      {/* Spirometry Results Card */}
      <SpirometryResultsCard uploadedSpirometry={uploadedSpirometry} bookingId={bookingId} />
    </div>
  );
};

// Connex Vitals Component
const ConnexVitalsCard = ({ vitalsData, getVitalStatus, getProgressPercentage }) => {
  if (!vitalsData) {
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Header with darker background */}
        <div className="bg-green-700 p-4 border-l-4 border-green-900">
          <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
            <Heart className="w-5 h-5" />
            <span>Connex Vital Signs</span>
          </h3>
        </div>
        <div className="p-6">
          <p className="text-gray-500 text-center py-8">Connex Vital Signs data isn't available yet for this appointment.</p>
        </div>
      </div>
    );
  }

  const vitals = [
    {
      icon: Heart,
      label: 'Heart Rate',
      value: vitalsData.pulse_rate,
      unit: 'bpm',
      min: 60,
      max: 100,
      color: 'text-red-500'
    },
    {
      icon: Wind,
      label: 'SpO2',
      value: vitalsData.spo2,
      unit: '%',
      min: 95,
      max: 100,
      color: 'text-blue-500'
    },
    {
      icon: Thermometer,
      label: 'Temperature',
      value: vitalsData.temperature,
      unit: vitalsData.temperature_unit || '°C',
      min: 36.1,
      max: 37.2,
      color: 'text-orange-500'
    },
    {
      icon: Droplets,
      label: 'Blood Pressure',
      value: `${vitalsData.systolic_bp}/${vitalsData.diastolic_bp}`,
      unit: 'mmHg',
      min: 120,
      max: 80,
      color: 'text-purple-500',
      customStatus: vitalsData.systolic_bp < 120 && vitalsData.diastolic_bp < 80 ? 'normal' : 'warning'
    },
    {
      icon: Wind,
      label: 'Respiratory Rate',
      value: vitalsData.respiratory_rate,
      unit: '/min',
      min: 12,
      max: 20,
      color: 'text-cyan-500'
    },
    {
      icon: TrendingUp,
      label: 'Height',
      value: vitalsData.height,
      unit: vitalsData.height_unit || 'cm',
      min: 150,
      max: 200,
      color: 'text-green-500'
    },
    {
      icon: Activity,
      label: 'Weight',
      value: vitalsData.weight,
      unit: vitalsData.weight_unit || 'kg',
      min: 50,
      max: 100,
      color: 'text-indigo-500'
    },
    {
      icon: BarChart3,
      label: 'BMI',
      value: vitalsData.bmi,
      unit: 'kg/m²',
      min: 18.5,
      max: 24.9,
      color: 'text-pink-500'
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header with darker background */}
      <div className="bg-green-700 p-4 border-l-4 border-green-900">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
            <Heart className="w-5 h-5" />
            <span>Connex Vital Signs</span>
          </h3>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse" />
            <span className="text-sm text-green-100 font-medium">Live</span>
            <span className="text-xs text-green-200">• Updated: {vitalsData.recorded_at ? new Date(vitalsData.recorded_at).toLocaleTimeString() : 'Just now'}</span>
          </div>
        </div>
      </div>

      {/* Vitals Grid - 4 vitals per row, 2 rows total */}
      <div className="p-4 grid grid-cols-4 gap-3">
        {vitals.map((vital, index) => {
          const status = vital.customStatus ? { status: vital.customStatus, color: vital.customStatus === 'normal' ? 'text-green-600' : 'text-yellow-600', bg: vital.customStatus === 'normal' ? 'bg-green-50' : 'bg-yellow-50', icon: vital.customStatus === 'normal' ? CheckCircle : AlertTriangle } : getVitalStatus(typeof vital.value === 'string' ? vital.min : vital.value, vital.min, vital.max);
          const Icon = vital.icon;
          const StatusIcon = status.icon;
          const progress = typeof vital.value === 'number' ? getProgressPercentage(vital.value, vital.min, vital.max) : 100;

          return (
            <div key={index} className={`p-3 rounded-lg border-2 ${status.bg} border-gray-200 hover:shadow-md transition-shadow`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-1">
                  <Icon className={`w-4 h-4 ${vital.color}`} />
                  <span className="text-xs font-medium text-gray-700">{vital.label}</span>
                </div>
                <StatusIcon className={`w-3 h-3 ${status.color}`} />
              </div>

              <div className="mb-2">
                <div className="text-xl font-bold text-gray-900">
                  {vital.value}
                  <span className="text-sm text-gray-500 ml-1">{vital.unit}</span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="mb-2">
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${status.status === 'normal' ? 'bg-green-500' : status.status === 'warning' ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between text-xs">
                <span className={`font-medium ${status.color}`}>
                  {status.status === 'normal' ? '✓' : status.status === 'warning' ? '⚠' : '🚨'}
                </span>
                <span className="text-gray-500 text-xs">
                  {vital.min}-{vital.max}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* All vitals are now displayed in the main grid above */}

      {/* Footer */}
      <div className="bg-gray-50 px-6 py-3 border-t">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>📅 Measurement: {vitalsData.recorded_at ? new Date(vitalsData.recorded_at).toLocaleString() : 'Current appointment'}</span>
          <div className="space-x-2">
            <button className="text-blue-600 hover:text-blue-700 font-medium">View History</button>
            <button className="text-blue-600 hover:text-blue-700 font-medium">Export PDF</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ECG Results Component
const ECGResultsCard = ({ ecgData, uploadedECG, bookingId }) => {
  // Helper function to convert UTC to local time
  const formatLocalTime = (utcDateString) => {
    if (!utcDateString) return 'Unknown';
    try {
      // Parse the UTC date string and convert to local time
      const date = new Date(utcDateString);
      // Format in local timezone with full date and time
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Unknown';
    }
  };

  const handleDownload = async () => {
    try {
      const response = await apiClient.get(`/ecg-results/download/${bookingId}`, {
        responseType: 'blob'
      });
      
      // Get filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'ecg_result.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      } else if (uploadedECG?.filename) {
        filename = uploadedECG.filename;
      }
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('ECG result downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download ECG result');
    }
  };

  const handleView = () => {
    if (uploadedECG?.view_url) {
      window.open(uploadedECG.view_url, '_blank');
    }
  };

  // Show uploaded file if available
  if (uploadedECG) {
    const fileSize = uploadedECG.file_size ? (uploadedECG.file_size / 1024 / 1024).toFixed(2) : 'Unknown';
    const uploadedAt = formatLocalTime(uploadedECG.uploaded_at);

    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Header with darker background */}
        <div className="bg-blue-700 p-4 border-l-4 border-blue-900">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
              <Activity className="w-5 h-5" />
              <span>ECG Results</span>
            </h3>
            <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full flex items-center space-x-1">
              <CheckCircle className="w-4 h-4" />
              <span>Available</span>
            </span>
          </div>
        </div>

        {/* Content - Uploaded File Display */}
        <div className="p-6 space-y-4">
          {/* File Info Card */}
          <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-5 border-2 border-blue-200">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                <div className="bg-blue-600 p-3 rounded-lg">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">{uploadedECG.filename}</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">File Type:</span>
                      <span className="ml-2 font-medium text-gray-900">{uploadedECG.file_type?.toUpperCase()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Size:</span>
                      <span className="ml-2 font-medium text-gray-900">{fileSize} MB</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Uploaded:</span>
                      <span className="ml-2 font-medium text-gray-900">{uploadedAt}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handleView}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
            >
              <Eye className="w-5 h-5" />
              <span>View Full Document</span>
            </button>
            <button
              onClick={handleDownload}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
            >
              <Download className="w-5 h-5" />
              <span>Download</span>
            </button>
          </div>

          {/* Info Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-2">
            <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-800">
              This is a manually uploaded ECG result. For automated ECG integration, please ensure the ECG workstation is properly configured.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show message if no ECG data available
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header with darker background */}
      <div className="bg-blue-700 p-4 border-l-4 border-blue-900">
        <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
          <Activity className="w-5 h-5" />
          <span>ECG Results</span>
        </h3>
      </div>
      <div className="p-6">
        <div className="text-center py-8 text-gray-500">
          <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p>ECG Result isn't available yet for this appointment.</p>
          <p className="text-sm text-gray-400 mt-2">ECG results will appear here once uploaded.</p>
        </div>
      </div>
    </div>
  );
};

// Spirometry Results Component
const SpirometryResultsCard = ({ uploadedSpirometry, bookingId }) => {
  // Helper function to convert UTC to local time
  const formatLocalTime = (utcDateString) => {
    if (!utcDateString) return 'Unknown';
    try {
      // Parse the UTC date string and convert to local time
      const date = new Date(utcDateString);
      // Format in local timezone with full date and time
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Unknown';
    }
  };

  const handleDownload = async () => {
    try {
      const response = await apiClient.get(`/spirometry-results/download/${bookingId}`, {
        responseType: 'blob'
      });
      
      // Get filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'spirometry_result.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      } else if (uploadedSpirometry?.filename) {
        filename = uploadedSpirometry.filename;
      }
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Spirometry result downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download Spirometry result');
    }
  };

  const handleView = () => {
    if (uploadedSpirometry?.view_url) {
      window.open(uploadedSpirometry.view_url, '_blank');
    }
  };

  // Show uploaded file if available
  if (uploadedSpirometry) {
    const fileSize = uploadedSpirometry.file_size ? (uploadedSpirometry.file_size / 1024 / 1024).toFixed(2) : 'Unknown';
    const uploadedAt = formatLocalTime(uploadedSpirometry.uploaded_at);

    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Header with darker background */}
        <div className="bg-teal-700 p-4 border-l-4 border-teal-900">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
              <Wind className="w-5 h-5" />
              <span>Spirometry Results</span>
            </h3>
            <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full flex items-center space-x-1">
              <CheckCircle className="w-4 h-4" />
              <span>Available</span>
            </span>
          </div>
        </div>

        {/* Content - Uploaded File Display */}
        <div className="p-6 space-y-4">
          {/* File Info Card */}
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-5 border-2 border-teal-200">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                <div className="bg-teal-600 p-3 rounded-lg">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">{uploadedSpirometry.filename}</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">File Type:</span>
                      <span className="ml-2 font-medium text-gray-900">{uploadedSpirometry.file_type?.toUpperCase()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Size:</span>
                      <span className="ml-2 font-medium text-gray-900">{fileSize} MB</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-500">Uploaded:</span>
                      <span className="ml-2 font-medium text-gray-900">{uploadedAt}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handleView}
              className="flex-1 bg-teal-600 hover:bg-teal-700 text-white py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
            >
              <Eye className="w-5 h-5" />
              <span>View Full Document</span>
            </button>
            <button
              onClick={handleDownload}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
            >
              <Download className="w-5 h-5" />
              <span>Download</span>
            </button>
          </div>

          {/* Info Note */}
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 flex items-start space-x-2">
            <AlertTriangle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-teal-800">
              This is a manually uploaded Spirometry result. Results will be displayed here once available.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show message if no Spirometry data available
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header with darker background */}
      <div className="bg-teal-700 p-4 border-l-4 border-teal-900">
        <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
          <Wind className="w-5 h-5" />
          <span>Spirometry Results</span>
        </h3>
      </div>
      <div className="p-6">
        <div className="text-center py-8 text-gray-500">
          <Wind className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p>Spirometry Result isn't available yet for this appointment.</p>
          <p className="text-sm text-gray-400 mt-2">Spirometry results will appear here once uploaded.</p>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticsPanel;
